/**
 * `GET /v1/me/summary` (TMMAPP-0008 / TMMAPP-0011, design §8.3) — the My Accounts hero tiles in
 * one read. Wire types from `src/api/generated/`; normalized into the module's domain type.
 * Zeros and empties for a person owning nothing.
 */
import { request } from './client';
import type {
  CashByCurrency as WireCashByCurrency,
  NextPlanned as WireNextPlanned,
  SummaryResponse as WireSummary,
} from './generated';

export interface CashByCurrency {
  currency: string;
  bankBalanceMinor: number;
}

export interface NextPlanned {
  occursOn: string;
  seriesId: number;
  payee: string | null;
  amountMinor: number;
  currency: string;
}

export interface Summary {
  cash: CashByCurrency[];
  operatingCurrency: string | null;
  allocatedMinor: number;
  unallocatedMinor: number;
  statementBalanceMinor: number;
  unallocatedLineCount: number;
  nextPlanned: NextPlanned | null;
  /** Accounts summed from 0 because their opening is pending (TMMAPI-0160 U2). */
  openingPendingAccountIds: number[];
}

function normalizeCash(w: WireCashByCurrency): CashByCurrency {
  return { currency: w.currency, bankBalanceMinor: w.bankBalanceMinor };
}

function normalizeNextPlanned(w: WireNextPlanned): NextPlanned {
  return {
    occursOn: w.occursOn,
    seriesId: w.seriesId,
    payee: w.payee ?? null,
    amountMinor: w.amountMinor,
    currency: w.currency,
  };
}

export function normalizeSummary(w: WireSummary): Summary {
  return {
    cash: w.cash.map(normalizeCash),
    operatingCurrency: w.operatingCurrency ?? null,
    allocatedMinor: w.allocatedMinor,
    unallocatedMinor: w.unallocatedMinor,
    statementBalanceMinor: w.statementBalanceMinor,
    unallocatedLineCount: w.unallocatedLineCount,
    nextPlanned: w.nextPlanned == null ? null : normalizeNextPlanned(w.nextPlanned),
    openingPendingAccountIds: w.openingPendingAccountIds,
  };
}

export const summaryApi = {
  /** GET /v1/me/summary. */
  async get(): Promise<Summary> {
    return normalizeSummary(await request<WireSummary>('GET', '/v1/me/summary'));
  },
};
